// Super Admin — Flexible Payment Plan module
//   /app/super-admin/installments               – summary dashboard + tabs
//   /app/super-admin/installments/plans         – plan CRUD
//   /app/super-admin/installments/enrollments   – enrollment list + actions
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, Plus, Pencil, Trash2, PauseCircle, PlayCircle, Send, AlertTriangle,
  ClipboardList, TrendingUp, ArrowRight, Search, ChevronDown, X, Mail,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { StatCard } from "@/components/StatCard";
import { api, formatApiError } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const EMPTY_PLAN = {
  course_id: "",
  plan_name: "",
  total_fee: 0,
  down_payment_amount: 0,
  number_of_installments: 3,
  installment_amount: 0,
  frequency: "monthly",
  custom_interval_days: 30,
  grace_period_days: 5,
  late_fee_enabled: false,
  late_fee_amount: 0,
  auto_pause_enabled: true,
  auto_resume_enabled: true,
  certificate_block_enabled: true,
  reminder_enabled: true,
  status: "active",
};


// ===================================================================
// DASHBOARD + TABS SHELL
// ===================================================================
export function AdminInstallmentsDashboardPage() {
  const [totals, setTotals] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/installment-reports");
        setTotals(data.data?.totals);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!totals) return <DashboardLayout title="Flexible Payment Plans"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title="Flexible Payment Plans" breadcrumbs={["Admin", "Billing", "Installments"]}>
      <div className="space-y-6" data-testid="admin-installment-dashboard">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon="Users"      label="Enrolments"      value={totals.enrolments} />
          <StatCard icon="TrendingUp" label="Revenue Paid"    value={inr(totals.revenue_paid)} tone="green" />
          <StatCard icon="Clock"      label="Pending Amount"  value={inr(totals.pending_amount)} tone="amber" />
          <StatCard icon="PauseCircle" label="Paused Access" value={totals.paused} tone="rose" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <StatCard icon="AlertTriangle" label="Grace Period"     value={totals.grace_period} />
          <StatCard icon="AlertTriangle" label="Overdue schedules" value={totals.overdue_schedules} />
          <StatCard icon="CheckCircle2"  label="Completed plans"   value={totals.completed_plans} tone="blue" />
        </div>
        <div className="flex gap-3">
          <Link to="/app/super-admin/installments/plans"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 hover:bg-slate-50"
            data-testid="link-plans">
            <ClipboardList className="h-4 w-4" /> Manage Plans <ArrowRight className="h-3 w-3" />
          </Link>
          <Link to="/app/super-admin/installments/enrollments"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 hover:bg-slate-50"
            data-testid="link-enrollments">
            <TrendingUp className="h-4 w-4" /> View Enrollments <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}


// ===================================================================
// PLANS PAGE (list + inline create/edit modal)
// ===================================================================
export function AdminInstallmentPlansPage() {
  const [rows, setRows] = useState(null);
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null); // plan object or "new" or null
  const [form, setForm] = useState(EMPTY_PLAN);
  const [busy, setBusy] = useState(false);
  // Reminder preview dialog
  const [previewPlan, setPreviewPlan] = useState(null); // plan object
  const [previewType, setPreviewType] = useState("upcoming"); // upcoming|due|overdue
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/installment-plans");
      setRows(data.data?.plans || []);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  const loadCourses = async () => {
    try {
      const { data } = await api.get("/admin/courses?limit=200");
      setCourses(data.data?.courses || data.data?.items || []);
    } catch { setCourses([]); }
  };
  useEffect(() => { load(); loadCourses(); }, []);

  // Fetch reminder preview when dialog opens or type changes
  useEffect(() => {
    if (!previewPlan) return;
    (async () => {
      setPreviewLoading(true);
      try {
        const { data } = await api.get(
          `/admin/installment-plans/${previewPlan.id}/reminder-preview`,
          { params: { type: previewType } },
        );
        setPreviewData(data.data);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setPreviewLoading(false); }
    })();
  }, [previewPlan, previewType]);

  const openNew = () => { setForm(EMPTY_PLAN); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      course_id: p.course_id, plan_name: p.plan_name, total_fee: p.total_fee,
      down_payment_amount: p.down_payment_amount, number_of_installments: p.number_of_installments,
      installment_amount: p.installment_amount, frequency: p.frequency,
      custom_interval_days: p.custom_interval_days || 30,
      grace_period_days: p.grace_period_days, late_fee_enabled: !!p.late_fee_enabled,
      late_fee_amount: p.late_fee_amount || 0,
      auto_pause_enabled: !!p.auto_pause_enabled, auto_resume_enabled: !!p.auto_resume_enabled,
      certificate_block_enabled: !!p.certificate_block_enabled, reminder_enabled: !!p.reminder_enabled,
      status: p.status,
    });
    setEditing(p);
  };
  const close = () => { setEditing(null); setForm(EMPTY_PLAN); };

  const save = async () => {
    // sanity check
    const expected = +form.down_payment_amount + (form.number_of_installments - 1) * +form.installment_amount;
    if (Math.abs(expected - +form.total_fee) > 1) {
      return toast.error(`Math mismatch: ${form.down_payment_amount} + ${form.number_of_installments - 1}×${form.installment_amount} = ${expected.toFixed(2)} ≠ ${form.total_fee}`);
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        total_fee: +form.total_fee, down_payment_amount: +form.down_payment_amount,
        installment_amount: +form.installment_amount, number_of_installments: +form.number_of_installments,
        grace_period_days: +form.grace_period_days,
        custom_interval_days: +form.custom_interval_days,
        late_fee_amount: +form.late_fee_amount,
      };
      if (editing === "new") {
        await api.post("/admin/installment-plans", payload);
        toast.success("Plan created");
      } else {
        await api.put(`/admin/installment-plans/${editing.id}`, payload);
        toast.success("Plan updated");
      }
      close(); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (p) => {
    try {
      await api.patch(`/admin/installment-plans/${p.id}/status`, {
        status: p.status === "active" ? "inactive" : "active",
      });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (rows === null) return <DashboardLayout title="Installment Plans"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title="Installment Plans" breadcrumbs={["Admin", "Billing", "Installments", "Plans"]}>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{rows.length} plans</div>
        <button onClick={openNew} data-testid="admin-add-plan-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-[#101876] px-4 py-2 text-sm font-bold text-white hover:bg-[#0d1360]">
          <Plus className="h-4 w-4" /> New plan
        </button>
      </div>

      {rows.length === 0 ? (
        <EmptyState message="No installment plans yet. Create one to enable EMI checkout on a course." />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white" data-testid="admin-plans-list">
          <ul className="divide-y divide-slate-100">
            {rows.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`plan-row-${p.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-black text-slate-900">{p.plan_name}</div>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.status}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {p.course?.course_title || p.course_id} · {p.enrolment_count} enrolments
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-700">
                    {inr(p.down_payment_amount)} down · {p.number_of_installments - 1} × {inr(p.installment_amount)} {p.frequency}
                  </div>
                </div>
                <button onClick={() => toggleStatus(p)} title="Toggle active"
                  className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50"
                  data-testid={`plan-toggle-${p.id}`}>
                  <span className={`inline-block h-2 w-2 rounded-full ${p.status === "active" ? "bg-emerald-500" : "bg-slate-300"}`} />
                </button>
                <button onClick={() => { setPreviewPlan(p); setPreviewType("upcoming"); setPreviewData(null); }}
                  title="Preview reminder email"
                  className="rounded-lg border border-sky-200 bg-sky-50 p-2 hover:bg-sky-100"
                  data-testid={`plan-preview-email-${p.id}`}>
                  <Mail className="h-4 w-4 text-sky-700" />
                </button>
                <button onClick={() => openEdit(p)}
                  className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50"
                  data-testid={`plan-edit-${p.id}`}>
                  <Pencil className="h-4 w-4 text-slate-700" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="plan-form-modal">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm font-black text-slate-900">{editing === "new" ? "New plan" : "Edit plan"}</div>
              <button onClick={close} data-testid="plan-form-close" className="rounded-lg p-1 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Course">
                <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })}
                  data-testid="plan-course" className={inputCls}>
                  <option value="">Select course…</option>
                  {courses.map((c) => <option key={c.id} value={c.id}>{c.course_title} ({c.course_code})</option>)}
                </select>
              </Field>
              <Field label="Plan name">
                <input value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                  data-testid="plan-name" className={inputCls} placeholder="3 Month Easy Plan" />
              </Field>
              <Field label="Total fee (₹)">
                <input type="number" value={form.total_fee} onChange={(e) => setForm({ ...form, total_fee: e.target.value })}
                  data-testid="plan-total" className={inputCls} />
              </Field>
              <Field label="Down payment (₹)">
                <input type="number" value={form.down_payment_amount} onChange={(e) => setForm({ ...form, down_payment_amount: e.target.value })}
                  data-testid="plan-down" className={inputCls} />
              </Field>
              <Field label="Number of installments (incl down)">
                <input type="number" min={2} max={24} value={form.number_of_installments} onChange={(e) => setForm({ ...form, number_of_installments: e.target.value })}
                  data-testid="plan-count" className={inputCls} />
              </Field>
              <Field label="Installment amount (₹)">
                <input type="number" value={form.installment_amount} onChange={(e) => setForm({ ...form, installment_amount: e.target.value })}
                  data-testid="plan-inst-amount" className={inputCls} />
              </Field>
              <Field label="Frequency">
                <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  data-testid="plan-frequency" className={inputCls}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="custom">Custom (days)</option>
                </select>
              </Field>
              {form.frequency === "custom" && (
                <Field label="Custom interval (days)">
                  <input type="number" value={form.custom_interval_days} onChange={(e) => setForm({ ...form, custom_interval_days: e.target.value })}
                    className={inputCls} />
                </Field>
              )}
              <Field label="Grace period (days)">
                <input type="number" value={form.grace_period_days} onChange={(e) => setForm({ ...form, grace_period_days: e.target.value })}
                  data-testid="plan-grace" className={inputCls} />
              </Field>
              <Field label="Late fee (placeholder, ₹)">
                <input type="number" value={form.late_fee_amount} onChange={(e) => setForm({ ...form, late_fee_amount: e.target.value })}
                  className={inputCls} />
              </Field>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs">
              {[
                ["auto_pause_enabled", "Auto-pause on overdue"],
                ["auto_resume_enabled", "Auto-resume on payment"],
                ["certificate_block_enabled", "Block certificate until fully paid"],
                ["reminder_enabled", "Send reminders"],
                ["late_fee_enabled", "Late-fee enabled (placeholder)"],
              ].map(([k, l]) => (
                <label key={k} className="inline-flex items-center gap-2 font-semibold text-slate-700">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                    data-testid={`plan-${k}`} />
                  {l}
                </label>
              ))}
              <label className="inline-flex items-center gap-2 font-semibold text-slate-700 sm:col-span-2">
                <input type="checkbox" checked={form.status === "active"}
                  onChange={(e) => setForm({ ...form, status: e.target.checked ? "active" : "inactive" })}
                  data-testid="plan-active" />
                Active
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={close} className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={busy}
                data-testid="plan-save"
                className="rounded-lg bg-[#101876] px-5 py-2 text-xs font-bold text-white hover:bg-[#0d1360] disabled:opacity-60">
                {busy ? "Saving…" : "Save plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="reminder-preview-modal">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">Reminder email preview</div>
                <div className="text-sm font-black text-slate-900">{previewPlan.plan_name}</div>
                <div className="text-[11px] text-slate-500">{previewPlan.course?.course_title || previewPlan.course_id}</div>
              </div>
              <button onClick={() => { setPreviewPlan(null); setPreviewData(null); }}
                data-testid="reminder-preview-close"
                className="rounded-lg p-1 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="border-b border-slate-100 bg-slate-50 px-5 py-2 flex gap-1">
              {["upcoming", "due", "overdue"].map((t) => (
                <button key={t} onClick={() => setPreviewType(t)}
                  data-testid={`reminder-preview-tab-${t}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-bold capitalize transition ${
                    previewType === t
                      ? "bg-[#101876] text-white"
                      : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
                  }`}>
                  {t === "upcoming" ? "3 days before" : t === "due" ? "On due date" : "Overdue / Paused"}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {previewLoading ? (
                <div className="py-10 text-center text-xs text-slate-500">Loading preview…</div>
              ) : previewData ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Subject</div>
                    <div className="text-sm font-bold text-slate-900" data-testid="reminder-preview-subject">{previewData.subject}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white">
                    <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                      <span>Email body (rendered)</span>
                      <span className="normal-case tracking-normal font-normal text-slate-500">
                        {previewData.uses_db_template ? "Using custom template from Notification Settings" : "Using built-in default template"}
                      </span>
                    </div>
                    <div className="p-4 text-sm text-slate-800 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: previewData.html }}
                      data-testid="reminder-preview-body" />
                  </div>
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900">
                    Sample variables (Rahul Sharma / ₹{previewData.variables?.amount_due} / {previewData.variables?.due_date}) used only for the preview. Actual emails use the real learner's data.
                  </div>
                </div>
              ) : (
                <div className="py-10 text-center text-xs text-rose-500">Could not load preview.</div>
              )}
            </div>
            <div className="flex justify-end border-t border-slate-100 bg-slate-50 px-5 py-3">
              <button onClick={() => { setPreviewPlan(null); setPreviewData(null); }}
                className="rounded-lg bg-[#101876] px-5 py-2 text-xs font-bold text-white hover:bg-[#0d1360]">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none";

function Field({ label, children }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-bold text-slate-700">{label}</span>
      {children}
    </label>
  );
}


// ===================================================================
// ENROLLMENTS PAGE (list + pause/resume/remind actions)
// ===================================================================
export function AdminInstallmentEnrollmentsPage() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState({ access_status: "", payment_plan_status: "" });

  const load = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => v && params.set(k, v));
      const { data } = await api.get(`/admin/installment-enrollments?${params.toString()}`);
      setRows(data.data?.enrollments || []);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, [filter.access_status, filter.payment_plan_status]);

  const act = async (e, action) => {
    const url = {
      pause:  `/admin/installment-enrollments/${e.id}/pause-access`,
      resume: `/admin/installment-enrollments/${e.id}/resume-access`,
      remind: `/admin/installment-enrollments/${e.id}/send-reminder`,
    }[action];
    try {
      const body = action === "remind" ? undefined : { remarks: `admin_${action}` };
      const req = action === "remind" ? api.post(url) : api.patch(url, body);
      await req;
      toast.success({ pause: "Access paused", resume: "Access resumed", remind: "Reminder sent" }[action]);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (rows === null) return <DashboardLayout title="Installment Enrollments"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title="Installment Enrollments" breadcrumbs={["Admin", "Billing", "Installments", "Enrollments"]}>
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="enrol-filters">
        <select value={filter.access_status} onChange={(e) => setFilter({ ...filter, access_status: e.target.value })}
          data-testid="filter-access" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs">
          <option value="">All access</option>
          <option value="active">Active</option>
          <option value="grace_period">Grace period</option>
          <option value="paused">Paused</option>
          <option value="completed_payment">Completed</option>
        </select>
        <select value={filter.payment_plan_status} onChange={(e) => setFilter({ ...filter, payment_plan_status: e.target.value })}
          data-testid="filter-plan-status" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs">
          <option value="">All plan status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="defaulted">Defaulted</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div className="ml-auto text-xs font-semibold text-slate-500">{rows.length} enrolments</div>
      </div>

      {rows.length === 0 ? (
        <EmptyState message="No enrolments match the filters." />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white" data-testid="admin-enrolments-list">
          <ul className="divide-y divide-slate-100">
            {rows.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`admin-enrol-row-${e.id}`}>
                <div className="flex-1 min-w-[220px]">
                  <div className="text-sm font-black text-slate-900">
                    {e.user?.full_name} · <span className="font-semibold text-slate-500">{e.user?.email}</span>
                  </div>
                  <div className="text-[11px] text-slate-500">{e.course?.course_title} · {e.plan?.plan_name}</div>
                </div>
                <div className="min-w-[120px] text-xs">
                  <div className="font-mono font-bold">{inr(e.amount_paid)} / {inr(e.total_amount)}</div>
                  <div className="text-slate-500">Pending {inr(e.pending_amount)}</div>
                </div>
                <div className="min-w-[110px]">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    e.access_status === "active" ? "bg-emerald-50 text-emerald-700"
                    : e.access_status === "grace_period" ? "bg-amber-50 text-amber-700"
                    : e.access_status === "paused" ? "bg-rose-50 text-rose-700"
                    : "bg-slate-100 text-slate-500"
                  }`}>{e.access_status}</span>
                </div>
                <div className="min-w-[110px] text-[11px]">
                  {e.next_due ? (
                    <>
                      Next: #{e.next_due.installment_number}<br />
                      <span className="text-slate-500">{new Date(e.next_due.due_date).toLocaleDateString()}</span>
                    </>
                  ) : <span className="text-slate-400">—</span>}
                </div>
                <div className="flex gap-1">
                  <IconBtn onClick={() => act(e, "remind")} title="Send reminder" testId={`remind-${e.id}`}><Send className="h-3.5 w-3.5" /></IconBtn>
                  {e.access_status !== "paused" ? (
                    <IconBtn onClick={() => act(e, "pause")} title="Pause access" testId={`pause-${e.id}`}><PauseCircle className="h-3.5 w-3.5" /></IconBtn>
                  ) : (
                    <IconBtn onClick={() => act(e, "resume")} title="Resume access" testId={`resume-${e.id}`}><PlayCircle className="h-3.5 w-3.5" /></IconBtn>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardLayout>
  );
}

function IconBtn({ children, onClick, title, testId }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId}
      className="rounded-lg border border-slate-200 p-2 text-slate-700 hover:bg-slate-50">
      {children}
    </button>
  );
}
